/* @flow */
import { deleteAgentIncentive } from '../../../services/IncentiveService';
import { filter, size } from 'lodash';
import { recalculateIncentiveReviewSummary } from '../../../utils/incentiveUtils';
import { useMutation, useQueryClient } from 'react-query';
import CommissionsServiceKeys from '../../../constants/CommissionsServiceKeys';
import useQueryCacheKey from '../../useQueryCacheKey';
import type { AgentIncentive } from '../../../models/incentives/AgentIncentive';

type AgentIncentiveDeleteMutationOptions = $ReadOnly<{
  onSuccess?: () => void,
  onError?: (error: any) => void,
}>;

type AgentIncentiveDeleteMutation = $ReadOnly<{
  mutateAsync: (
    data: AgentIncentive,
    options?: AgentIncentiveDeleteMutationOptions,
  ) => Promise<void>,
  isLoading: boolean,
}>;

export default function useDeleteAgentIncentive(): AgentIncentiveDeleteMutation {
  const queryClient = useQueryClient();
  const cacheKey = useQueryCacheKey(CommissionsServiceKeys.AGENT_INCENTIVES);

  const { mutateAsync, isLoading } = useMutation(
    async (agentIncentive: AgentIncentive) =>
      deleteAgentIncentive(agentIncentive.id, agentIncentive.payPeriodId),
    {
      onSuccess: async (response, deletedAgentIncentive: AgentIncentive) => {
        const queryKey = [...cacheKey, { payPeriodId: deletedAgentIncentive.payPeriodId }];

        await queryClient.cancelQueries(queryKey);
        const cachedIncentive = queryClient.getQueryData(queryKey);

        for (let teamIncentive of cachedIncentive.teamIncentivesReviews) {
          const index = teamIncentive?.agentIncentives?.findIndex(
            (item) => item.id === deletedAgentIncentive.id,
          );
          if (index >= 0) {
            teamIncentive.agentIncentives.splice(index, 1);

            teamIncentive.teamIncentivesSummary = {
              totalIncentivesAmount:
                teamIncentive.teamIncentivesSummary.totalIncentivesAmount -
                deletedAgentIncentive.amount,
              numberOfAgents: teamIncentive.teamIncentivesSummary.numberOfAgents - 1,
              numberOfAgentsApproved: size(
                filter(
                  teamIncentive.agentIncentives,
                  (agentIncentive: AgentIncentive) => agentIncentive.isApproved === true,
                ),
              ),
            };

            if (teamIncentive.agentIncentives.length <= 0) {
              cachedIncentive.teamIncentivesReviews.splice(
                cachedIncentive.teamIncentivesReviews.findIndex(
                  (group) => group.teamId === teamIncentive.teamId,
                ),
                1,
              );
            }
          }
        }

        cachedIncentive.incentiveReviewSummary = recalculateIncentiveReviewSummary(cachedIncentive);

        queryClient.setQueryData(queryKey, () => cachedIncentive);
        return cachedIncentive;
      },
    },
  );
  return {
    mutateAsync: async (
      data: AgentIncentive,
      options?: AgentIncentiveDeleteMutationOptions,
    ): Promise<void> => {
      try {
        await mutateAsync(data);
        options?.onSuccess?.();
      } catch (e) {
        options?.onError?.(e);
      }
    },
    isLoading,
  };
}
